<template>
  <div
    v-if="visible"
    class="overlay"
    @click.self="$emit('close')"
  >
    <div class="ajanlat-ar-modal">
      <div class="modal-header">
        <h2>Projekt ár importálás</h2>
        <button
          type="button"
          class="close-btn"
          @click="$emit('close')"
        >
          x
        </button>
      </div>

      <div class="step-bar">
        <div
          class="step"
          :class="{ active: step === 1, done: step > 1 }"
        >
          <span class="step-num">1</span>
          Projekt
        </div>
        <div
          class="step"
          :class="{ active: step === 2, done: step > 2 }"
        >
          <span class="step-num">2</span>
          Egyezések
        </div>
        <div
          class="step"
          :class="{ active: step === 3, done: step > 3 }"
        >
          <span class="step-num">3</span>
          Kész
        </div>
      </div>

      <div class="modal-body">
        <div v-if="step === 1">
          <div class="section-label">
            Forrás projekt kiválasztása
          </div>
          <div class="search-box">
            <span>⌕</span>
            <input
              v-model="keresoSzo"
              type="text"
              placeholder="Keresés projekt név alapján"
            >
          </div>
          <div class="project-list">
            <div
              v-for="projekt in szurtProjektek"
              :key="sourceKey(projekt)"
              class="project-item"
              :class="{ selected: selectedSourceKey === sourceKey(projekt) }"
              @click="selectProjekt(projekt)"
            >
              <div>
                <div class="proj-name">
                  {{ projekt.nev }}
                </div>
                <div class="proj-meta">
                  {{ projektMeta(projekt) }} • {{ projekt.datum_tol || '-' }} - {{ projekt.datum_ig || '-' }}
                </div>
              </div>
              <div class="proj-meta">
                {{ offerTypeLabel(projekt.forras_offer_type) }} #{{ projekt.projekt_id }}
              </div>
            </div>
            <div
              v-if="!loading && !szurtProjektek.length"
              class="empty-state"
            >
              Nincs importálható ár ehhez a munkafázishoz.
            </div>
            <div
              v-if="loading"
              class="empty-state"
            >
              Betöltés...
            </div>
          </div>
        </div>

        <div v-if="step === 2">
          <div class="section-label">
            Egyező tételek
          </div>
          <table class="match-table">
            <thead>
              <tr>
                <th>Tétel</th>
                <th>Egyezés</th>
                <th>Jelenlegi</th>
                <th>Forrás ár</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="tetel in egyezoTetelek"
                :key="`match-${tetel.tetel_id}`"
              >
                <td>{{ tetel.megnevezes }}</td>
                <td>
                  <span
                    class="badge-egyezes"
                    :class="badgeClass(tetel.egyezes_tipusa)"
                  >
                    {{ badgeText(tetel.egyezes_tipusa) }}
                  </span>
                </td>
                <td>
                  <div class="old-price">
                    A: {{ formatFt(tetel.jelenlegi_anyag_ar) }}
                  </div>
                  <div class="old-price">
                    D: {{ formatFt(tetel.jelenlegi_dij_ar) }}
                  </div>
                </td>
                <td>
                  <div class="new-price">
                    A: {{ formatFt(tetel.forras_anyag_ar) }}
                  </div>
                  <div class="new-price">
                    D: {{ formatFt(tetel.forras_dij_ar) }}
                  </div>
                </td>
              </tr>
              <tr
                v-if="!egyezoTetelek.length"
                class="no-match"
              >
                <td colspan="4">
                  Nincs automatikus egyezés.
                </td>
              </tr>
            </tbody>
          </table>

          <div
            v-if="hianyzoTetelek.length"
            class="found-elsewhere"
          >
            <div class="fe-title">
              Hiányzó tételek, amelyek más projektben megtalálhatók
            </div>
            <div
              v-for="tetel in hianyzoTetelek"
              :key="`missing-${tetel.tetel_id}`"
            >
              <div class="fe-teteI">
                {{ tetel.megnevezes }}
              </div>
              <span
                v-for="projekt in tetel.talalhato_projektekben"
                :key="`chip-${tetel.tetel_id}-${sourceKey(projekt)}`"
                class="proj-chip"
                :class="{ 'chip-selected': chipValasztasok[tetel.tetel_id] === sourceKey(projekt) }"
                @click="chipKivalaszt(tetel.tetel_id, projekt)"
              >
                {{ projekt.projekt_nev }} • {{ formatFt(projekt.anyag_egysegar) }} / {{ formatFt(projekt.alap_dij_egysegre) }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="step === 3">
          <div class="done-state">
            <div class="done-icon">
              ✓
            </div>
            <div>
              <div class="done-title">
                Importálás kész
              </div>
              <div class="done-text">
                {{ frissitett }} tétel ára frissítve.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="loading || step === 1"
          @click="step -= 1"
        >
          Vissza
        </button>
        <div>
          <button
            type="button"
            class="btn btn-secondary footer-gap"
            :disabled="loading"
            @click="$emit('close')"
          >
            Mégse
          </button>
          <button
            v-if="step === 1"
            type="button"
            class="btn btn-primary"
            :disabled="loading || !selectedProjektId || !selectedForrasOfferType"
            @click="ellenorizEgyezesek"
          >
            {{ loading ? 'Betöltés...' : 'Tovább' }}
          </button>
          <button
            v-else-if="step === 2"
            type="button"
            class="btn btn-primary"
            :disabled="loading || !importTetelek.length"
            @click="vegrehajt"
          >
            {{ loading ? 'Importálás...' : 'Importálás' }}
          </button>
          <button
            v-else
            type="button"
            class="btn btn-primary"
            @click="$emit('close')"
          >
            Bezárás
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AjanlatArImportModal',
  props: {
    munkafazis_id: {
      type: [Number, String],
      required: true,
    },
    offerId: {
      type: [Number, String],
      required: true,
    },
    offerType: {
      type: String,
      default: 'project',
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: 1,
      projektek: [],
      selectedProjektId: null,
      selectedForrasOfferType: '',
      keresoSzo: '',
      egyezoTetelek: [],
      hianyzoTetelek: [],
      chipValasztasok: {},
      loading: false,
      frissitett: 0,
    }
  },
  computed: {
    szurtProjektek() {
      const q = String(this.keresoSzo || '').trim().toLowerCase()
      if (!q) return this.projektek
      return this.projektek.filter(projekt => String(projekt.nev || '').toLowerCase().includes(q))
    },
    importTetelek() {
      return this.egyezoTetelek.map(tetel => ({
        tetel_id: tetel.tetel_id,
        forras_offer_type: this.selectedForrasOfferType,
        forras_projekt_id: this.selectedProjektId,
      })).concat(Object.entries(this.chipValasztasok).map(([tid, source]) => {
        const parsed = this.parseSourceKey(source)
        return {
          tetel_id: Number(tid),
          forras_offer_type: parsed.type,
          forras_projekt_id: parsed.id,
        }
      }))
    },
    selectedSourceKey() {
      if (!this.selectedForrasOfferType || !this.selectedProjektId) return ''
      return `${this.selectedForrasOfferType}:${this.selectedProjektId}`
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.reset()
        this.betoltProjektek()
      }
    },
  },
  methods: {
    reset() {
      this.step = 1
      this.projektek = []
      this.selectedProjektId = null
      this.selectedForrasOfferType = ''
      this.keresoSzo = ''
      this.egyezoTetelek = []
      this.hianyzoTetelek = []
      this.chipValasztasok = {}
      this.frissitett = 0
    },
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
    },
    betoltProjektek() {
      this.loading = true
      axios({
        method: 'get',
        url: 'ajanlat/projektek-arral',
        params: {
          munkafazis_id: this.munkafazis_id,
          offer_id: this.offerId,
          offer_type: this.offerType,
        },
        headers: this.authHeaders(),
      }).then(response => {
        this.projektek = Array.isArray(response.data) ? response.data : []
      }).finally(() => {
        this.loading = false
      })
    },
    ellenorizEgyezesek() {
      this.loading = true
      axios({
        method: 'post',
        url: 'ajanlat/ar-egyezes',
        headers: this.authHeaders(),
        data: {
          munkafazis_id: this.munkafazis_id,
          offer_id: this.offerId,
          offer_type: this.offerType,
          forras_offer_type: this.selectedForrasOfferType,
          forras_projekt_id: this.selectedProjektId,
        },
      }).then(response => {
        this.egyezoTetelek = Array.isArray(response.data?.egyezo) ? response.data.egyezo : []
        this.hianyzoTetelek = Array.isArray(response.data?.hianyzok) ? response.data.hianyzok : []
        this.step = 2
      }).finally(() => {
        this.loading = false
      })
    },
    vegrehajt() {
      this.loading = true
      axios({
        method: 'post',
        url: 'ajanlat/ar-import',
        headers: this.authHeaders(),
        data: {
          munkafazis_id: this.munkafazis_id,
          offer_id: this.offerId,
          offer_type: this.offerType,
          tetelek: this.importTetelek,
        },
      }).then(response => {
        this.frissitett = Number(response.data?.frissitett || 0)
        this.$emit('imported', this.frissitett)
        this.step = 3
      }).finally(() => {
        this.loading = false
      })
    },
    selectProjekt(projekt) {
      this.selectedProjektId = projekt.projekt_id
      this.selectedForrasOfferType = projekt.forras_offer_type || 'project'
    },
    chipKivalaszt(tetelId, projekt) {
      this.$set(this.chipValasztasok, tetelId, this.sourceKey(projekt))
    },
    sourceKey(projekt) {
      const type = (projekt && projekt.forras_offer_type) || 'project'
      const id = (projekt && projekt.projekt_id) || ''
      return `${type}:${id}`
    },
    parseSourceKey(value) {
      const parts = String(value || '').split(':')
      return {
        type: parts[0] || 'project',
        id: Number(parts[1] || 0),
      }
    },
    offerTypeLabel(type) {
      return type === 'task' ? 'Feladat' : 'Projekt'
    },
    projektMeta(projekt) {
      const aktualis = Number((projekt && projekt.tetelek_szama) || 0)
      const osszes = Number((projekt && projekt.osszes_tetelek_szama) || aktualis || 0)
      if (aktualis > 0 && osszes > aktualis) {
        return `${aktualis} tétel ebben, ${osszes} összesen`
      }
      if (aktualis > 0) {
        return `${aktualis} tétel`
      }
      return `${osszes} tétel más munkafázisban`
    },
    badgeClass(type) {
      if (type === 'tetel_id') return 'b-tetel'
      if (type === 'pozicio') return 'b-pozicio'
      if (type === 'excel_sor') return 'b-excel'
      return 'b-szoveg'
    },
    badgeText(type) {
      const labels = {
        tetel_id: 'tétel ID',
        pozicio: 'pozíció',
        excel_sor: 'Excel sor',
        szoveg: 'szöveg',
      }
      return labels[type] || 'szöveg'
    },
    formatFt(value) {
      const number = Math.round(Number(value || 0))
      return `${number.toLocaleString('hu-HU')} Ft`
    },
  },
}
</script>

<style scoped>
* { box-sizing: border-box; margin: 0; padding: 0; }
.overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.65);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.ajanlat-ar-modal {
  background: #1a2332; border: 1px solid #2a3a50; border-radius: 12px;
  width: 720px; max-height: 90vh; overflow-y: auto;
  font-family: system-ui; color: #e2e8f0;
  display: block;
}
.modal-header {
  background: #0f1623; padding: 16px 20px;
  display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid #2a3a50; position: sticky; top: 0; z-index: 1;
}
.modal-header h2 { font-size: 15px; font-weight: 500; color: #e2e8f0; }
.close-btn { background: transparent; border: 0; color: #94a3b8; cursor: pointer; font-size: 18px; }
.step-bar {
  display: flex; background: #0f1623;
  border-bottom: 1px solid #2a3a50; padding: 0 20px;
  position: sticky; top: 53px; z-index: 1;
}
.step {
  padding: 10px 0; margin-right: 24px; font-size: 12px; color: #64748b;
  border-bottom: 2px solid transparent; display: flex; align-items: center; gap: 6px;
}
.step.active { color: #3b82f6; border-bottom-color: #3b82f6; }
.step.done   { color: #10b981; }
.step-num {
  width: 18px; height: 18px; border-radius: 50%; font-size: 11px;
  display: flex; align-items: center; justify-content: center;
  background: #2a3a50; color: #64748b;
}
.step.active .step-num { background: #3b82f6; color: #fff; }
.step.done   .step-num { background: #10b981; color: #fff; }
.modal-body { padding: 20px; }
.section-label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748b; margin-bottom: 8px;
}
.search-box {
  display: flex; align-items: center; gap: 8px;
  background: #0f1623; border: 1px solid #2a3a50;
  border-radius: 8px; padding: 0 12px; margin-bottom: 12px;
}
.search-box input {
  background: none; border: none; outline: none;
  color: #e2e8f0; font-size: 13px; padding: 10px 0; flex: 1;
}
.project-list { max-height: 220px; overflow-y: auto; margin-bottom: 12px; }
.project-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 9px 12px; border-radius: 8px; cursor: pointer;
  font-size: 13px; border: 1px solid transparent; margin-bottom: 4px;
}
.project-item:hover   { background: #243044; }
.project-item.selected { background: #1e3a5f; border-color: #3b82f6; }
.project-item .proj-name { color: #e2e8f0; }
.project-item .proj-meta { font-size: 11px; color: #64748b; }
.match-table { width: 100%; border-collapse: collapse; border: 1px solid #2a3a50; border-radius: 0 0 8px 8px; }
.match-table th {
  background: #152030; font-size: 11px; color: #64748b;
  padding: 7px 10px; text-align: left; font-weight: 500;
  border-bottom: 1px solid #2a3a50;
}
.match-table td {
  font-size: 12px; padding: 7px 10px;
  border-bottom: 1px solid #1e2d3d; color: #cbd5e1; vertical-align: middle;
}
.match-table tr.no-match td { background: #1f1510; }
.new-price  { color: #34d399; font-weight: 500; }
.old-price  { color: #94a3b8; font-size: 11px; }
.badge-egyezes {
  font-size: 10px; padding: 2px 6px; border-radius: 20px; font-weight: 500;
}
.b-tetel  { background: #064e3b; color: #34d399; }
.b-pozicio { background: #1e3a5f; color: #93c5fd; }
.b-excel  { background: #422006; color: #fb923c; }
.b-szoveg { background: #2a2a2a; color: #94a3b8; }
.found-elsewhere {
  background: #1a1a0f; border: 1px solid #713f12;
  border-radius: 8px; padding: 12px 14px; margin-top: 16px;
}
.fe-title { font-size: 12px; color: #fbbf24; margin-bottom: 10px; }
.fe-teteI { font-size: 12px; color: #e2e8f0; font-weight: 500; margin-bottom: 4px; }
.proj-chip {
  font-size: 11px; padding: 3px 10px; border-radius: 20px;
  background: #1e3a5f; color: #93c5fd; border: 1px solid #2563eb44;
  cursor: pointer; display: inline-block; margin: 2px;
}
.proj-chip.chip-selected { background: #1d4ed8; border-color: #3b82f6; color: #fff; }
.modal-footer {
  padding: 14px 20px; background: #0f1623;
  border-top: 1px solid #2a3a50;
  display: flex; align-items: center; justify-content: space-between;
  position: sticky; bottom: 0;
}
.btn { padding: 8px 18px; border-radius: 8px; font-size: 13px; cursor: pointer; border: none; font-weight: 500; }
.btn-secondary { background: #2a3a50; color: #94a3b8; }
.btn-primary   { background: #3b82f6; color: #fff; }
.btn-primary:disabled { background: #1e3a5f; color: #4a5568; cursor: not-allowed; }
.footer-gap { margin-right: 8px; }
.empty-state { color: #94a3b8; font-size: 12px; padding: 16px 4px; }
.done-state { display: flex; align-items: center; gap: 14px; padding: 24px 6px; }
.done-icon {
  width: 42px; height: 42px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: #064e3b; color: #34d399; font-weight: 700; font-size: 24px;
}
.done-title { color: #e2e8f0; font-size: 16px; font-weight: 600; }
.done-text { color: #94a3b8; font-size: 13px; margin-top: 4px; }
</style>
